import type { NavGrid } from '../../sim/navGrid';
import type { WaterRegion, ZoneLayout } from '../../zones/types';

/**
 * Rasterises a zone for the map panel: walkable ground tinted by the zone's
 * map colour with hill shading, roads in pale dust, water in deep teal and
 * everything blocked (cliffs, walls, trees) dark, with a gold rim along the
 * walkable edge. Computed once per zone and cached as a data URL.
 */

export interface ZoneRaster {
  url: string;
  /** Pixels per world metre. */
  ppm: number;
  w: number;
  h: number;
}

const cache = new Map<string, ZoneRaster>();
const MAX_SIDE = 640;

const rgb = (hex: number): [number, number, number] => [
  (hex >> 16) & 255,
  (hex >> 8) & 255,
  hex & 255,
];
const mix = (
  a: [number, number, number],
  b: [number, number, number],
  t: number,
): [number, number, number] => [
  a[0] + (b[0] - a[0]) * t,
  a[1] + (b[1] - a[1]) * t,
  a[2] + (b[2] - a[2]) * t,
];

function inWater(w: WaterRegion, x: number, z: number, h: number): boolean {
  if (h >= w.level) return false;
  const s = w.shape;
  if (s.kind === 'sheet') return true;
  if (s.kind === 'circle') return Math.hypot(x - s.x, z - s.z) <= s.r;
  return Math.abs(x - s.x) <= s.w / 2 && Math.abs(z - s.z) <= s.d / 2;
}

export function zoneRaster(layout: ZoneLayout, nav: NavGrid | null): ZoneRaster | null {
  const key = `${layout.id}:${nav ? 'nav' : 'plain'}`;
  const hit = cache.get(key);
  if (hit) return hit;
  if (typeof document === 'undefined') return null;
  const ppm = Math.min(2, MAX_SIDE / Math.max(layout.width, layout.depth));
  const w = Math.max(8, Math.round(layout.width * ppm));
  const h = Math.max(8, Math.round(layout.depth * ppm));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const g = canvas.getContext('2d');
  if (!g) return null;

  // Terrain is sampled on a coarse grid (surfaceAt is the expensive call) and
  // interpolated per pixel; the nav mask stays at full resolution.
  const step = 2;
  const gw = Math.ceil(w / step) + 1;
  const gh = Math.ceil(h / step) + 1;
  const gHeight = new Float32Array(gw * gh);
  const gRoad = new Float32Array(gw * gh);
  const gRock = new Float32Array(gw * gh);
  for (let gy = 0; gy < gh; gy++) {
    const z = layout.minZ + (gy * step + 0.5) / ppm;
    for (let gx = 0; gx < gw; gx++) {
      const x = layout.minX + (gx * step + 0.5) / ppm;
      const i = gy * gw + gx;
      gHeight[i] = layout.heightAt(x, z);
      const sf = layout.surfaceAt(x, z);
      const sum = sf[0] + sf[1] + sf[2] + sf[3] || 1;
      gRoad[i] = sf[1] / sum;
      gRock[i] = sf[2] / sum;
    }
  }
  const sample = (grid: Float32Array, px: number, py: number): number => {
    const fx = px / step;
    const fy = py / step;
    const x0 = Math.min(gw - 2, Math.floor(fx));
    const y0 = Math.min(gh - 2, Math.floor(fy));
    const tx = fx - x0;
    const ty = fy - y0;
    const a = grid[y0 * gw + x0] ?? 0;
    const b = grid[y0 * gw + x0 + 1] ?? 0;
    const c = grid[(y0 + 1) * gw + x0] ?? 0;
    const d = grid[(y0 + 1) * gw + x0 + 1] ?? 0;
    return (a * (1 - tx) + b * tx) * (1 - ty) + (c * (1 - tx) + d * tx) * ty;
  };
  const dark = layout.kind === 'dungeon' || layout.biome === 'abyss';
  const land = mix(rgb(layout.mapTint ?? 0x6a6a50), [214, 198, 160], dark ? 0.25 : 0.35);
  const road: [number, number, number] = dark ? [150, 136, 116] : [222, 206, 164];
  const rock: [number, number, number] = [124, 120, 112];
  const waterC: [number, number, number] = [30, 78, 88];
  const blocked: [number, number, number] = dark ? [16, 15, 15] : [26, 30, 28];
  const waterLevel = layout.water[0]?.level ?? 0;
  const img = g.createImageData(w, h);
  const walk = new Uint8Array(w * h);
  for (let py = 0; py < h; py++) {
    const z = layout.minZ + (py + 0.5) / ppm;
    for (let px = 0; px < w; px++) {
      const x = layout.minX + (px + 0.5) / ppm;
      const i = py * w + px;
      const hh = sample(gHeight, px, py);
      let col: [number, number, number];
      let alpha = 235;
      if (layout.water.some((wr) => inWater(wr, x, z, hh))) {
        const depth = Math.min(1, Math.max(0, waterLevel - hh) / 6);
        col = mix([54, 112, 118], waterC, depth);
      } else if (!nav || nav.walkable({ x, z })) {
        walk[i] = 1;
        col = mix(
          mix(land, rock, sample(gRock, px, py) * 0.55),
          road,
          sample(gRoad, px, py) * 0.75,
        );
        // Hill shade: light from the north-west.
        const slope =
          (sample(gHeight, px - 1, py) -
            sample(gHeight, px + 1, py) +
            sample(gHeight, px, py - 1) -
            sample(gHeight, px, py + 1)) *
          ppm *
          0.18;
        const shade = Math.max(-0.35, Math.min(0.35, slope));
        col = shade >= 0 ? mix(col, [255, 246, 220], shade) : mix(col, [0, 0, 0], -shade);
      } else {
        col = blocked;
        alpha = 215;
      }
      const o = i * 4;
      img.data[o] = col[0];
      img.data[o + 1] = col[1];
      img.data[o + 2] = col[2];
      img.data[o + 3] = alpha;
    }
  }
  // Gold rim where walkable ground meets blocked ground.
  for (let py = 1; py < h - 1; py++) {
    for (let px = 1; px < w - 1; px++) {
      const i = py * w + px;
      if (!walk[i]) continue;
      if (walk[i - 1] && walk[i + 1] && walk[i - w] && walk[i + w]) continue;
      const o = i * 4;
      img.data[o] = 201;
      img.data[o + 1] = 164;
      img.data[o + 2] = 90;
      img.data[o + 3] = 255;
    }
  }
  g.putImageData(img, 0, 0);
  const out: ZoneRaster = { url: canvas.toDataURL('image/png'), ppm, w, h };
  cache.set(key, out);
  return out;
}
